import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import type { ProfileInput } from "@/lib/validations/profile";
import { useTranslation } from "react-i18next";

export interface Profile {
  id: string;
  full_name: string;
  clinic_name?: string | null;
  current_clinic_id: string | null;
  phone: string | null;
  cpf: string | null;
  crmv_state: string | null;
  crmv_number: number | null;
  mapa_number: string | null;
  address: string | null;
  avatar_url: string | null;
  email: string;
  created_at: string;
  updated_at: string;
}

function isMapaNumberUniqueViolation(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;

  const maybe = error as { code?: string; message?: string; details?: string; hint?: string };
  const code = String(maybe.code ?? "");
  const msg = `${maybe.message ?? ""} ${maybe.details ?? ""} ${maybe.hint ?? ""}`.toLowerCase();

  const isUnique = code === "23505" || msg.includes("duplicate key");
  if (!isUnique) return false;

  return msg.includes("profiles_mapa_number_unique") || msg.includes("mapa_number");
}

function isCrmvUniqueViolation(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;

  const maybe = error as { code?: string; message?: string; details?: string; hint?: string };
  const code = String(maybe.code ?? "");
  const msg = `${maybe.message ?? ""} ${maybe.details ?? ""} ${maybe.hint ?? ""}`.toLowerCase();

  const isUnique = code === "23505" || msg.includes("duplicate key");
  if (!isUnique) return false;

  return msg.includes("profiles_crmv_unique") || msg.includes("crmv_state") || msg.includes("crmv_number");
}

// Buscar perfil do usuário atual
export const useProfile = () => {
  const { user } = useAuth();

  return useQuery<Profile | null>({
    queryKey: ["profile", user?.id],
    queryFn: async () => {
      if (!user) return null;

      // Buscar dados do perfil
      const { data: profile, error: profileError } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", user.id)
        .single();

      if (profileError) throw profileError;

      // Combinar dados do perfil com email do auth
      return {
        ...profile,
        email: user.email || "",
      } as Profile;
    },
    enabled: !!user,
  });
};

// Atualizar perfil do usuário
export const useUpdateProfile = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user } = useAuth();
  const { t } = useTranslation("settings");

  return useMutation({
    mutationFn: async (input: ProfileInput) => {
      if (!user) throw new Error(t("phase8.profile.errors.notAuthenticated"));

      // Atualizar email no auth (se mudou)
      if (input.email !== user.email) {
        const { error: emailError } = await supabase.auth.updateUser({
          email: input.email,
        });

        if (emailError) {
          throw new Error(
            emailError.message || t("phase8.profile.errors.updateEmailFailed")
          );
        }
      }

      // Atualizar dados do perfil
      const crmvState = input.crmv_state?.trim().toUpperCase();
      const mapaNumber = input.mapa_number?.trim();
      const updateData: {
        full_name: string;
        phone?: string | null;
        cpf?: string | null;
        crmv_state?: string | null;
        crmv_number?: number | null;
        mapa_number?: string | null;
        address?: string | null;
      } = {
        full_name: input.full_name,
        phone: input.phone || null,
        cpf: input.cpf || null,
        crmv_state: crmvState ? crmvState : null,
        crmv_number: input.crmv_number || null,
        mapa_number: mapaNumber ? mapaNumber : null,
        address: input.address?.trim() || null,
      };

      const { error: profileError } = await supabase
        .from("profiles")
        .update(updateData as never)
        .eq("id", user.id);

      if (profileError) {
        if (isMapaNumberUniqueViolation(profileError)) {
          throw new Error(t("phase8.profile.errors.mapaAlreadyUsed"));
        }
        if (isCrmvUniqueViolation(profileError)) {
          throw new Error(t("phase8.profile.errors.crmvAlreadyUsed"));
        }
        throw new Error(profileError.message || t("phase8.profile.errors.updateFailed"));
      }

      return { success: true };
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["profile"] });
      queryClient.invalidateQueries({ queryKey: ["profile", user?.id] });

      // Se o email foi alterado, mostrar mensagem de confirmação
      if (variables.email !== user?.email) {
        toast({
          title: t("phase8.profile.emailUpdatedTitle"),
          description: t("phase8.profile.emailUpdatedDescription"),
        });
      } else {
        toast({
          title: t("phase8.profile.updatedTitle"),
          description: t("phase8.profile.updatedDescription"),
        });
      }
    },
    onError: (error: Error) => {
      toast({
        title: t("phase8.profile.updateErrorTitle"),
        description: error.message || t("phase8.profile.errors.updateFailed"),
        variant: "destructive",
      });
    },
  });
};

// Upload de foto de perfil
export const useUploadProfilePhoto = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user } = useAuth();
  const { t } = useTranslation("settings");

  return useMutation({
    mutationFn: async (file: File) => {
      if (!user) throw new Error(t("phase8.profile.errors.notAuthenticated"));

      // Validar tipo de arquivo
      const validTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
      if (!validTypes.includes(file.type)) {
        throw new Error(t("phase8.profile.errors.invalidImageType"));
      }

      // Validar tamanho (5MB)
      if (file.size > 5 * 1024 * 1024) {
        throw new Error(t("phase8.profile.errors.imageTooLarge"));
      }

      // Buscar foto anterior para deletar
      const { data: currentProfile } = await supabase
        .from("profiles")
        .select("avatar_url")
        .eq("id", user.id)
        .single();

      // Deletar foto anterior se existir
      if (currentProfile?.avatar_url) {
        // Extrair o nome do arquivo da URL
        const urlParts = currentProfile.avatar_url.split("/");
        const fileName = urlParts[urlParts.length - 1];
        const filePath = `${user.id}/${fileName}`;

        await supabase.storage.from("profile-photos").remove([filePath]);
      }

      // Gerar nome único para o arquivo
      const fileExt = file.name.split(".").pop();
      const fileName = `${user.id}/avatar.${fileExt}`;

      // Upload para storage
      const { error: uploadError } = await supabase.storage
        .from("profile-photos")
        .upload(fileName, file, {
          upsert: true,
        });

      if (uploadError) throw uploadError;

      // Obter URL pública
      const { data: urlData } = supabase.storage
        .from("profile-photos")
        .getPublicUrl(fileName);

      // Atualizar avatar_url no perfil
      const { error: updateError } = await supabase
        .from("profiles")
        .update({ avatar_url: urlData.publicUrl })
        .eq("id", user.id);

      if (updateError) throw updateError;

      return urlData.publicUrl;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["profile"] });
      toast({
        title: t("phase8.profile.photoUpdatedTitle"),
        description: t("phase8.profile.photoUpdatedDescription"),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t("phase8.profile.photoUploadErrorTitle"),
        description: error.message || t("phase8.profile.errors.photoUpdateFailed"),
        variant: "destructive",
      });
    },
  });
};

// Deletar foto de perfil
export const useDeleteProfilePhoto = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user } = useAuth();
  const { t } = useTranslation("settings");

  return useMutation({
    mutationFn: async () => {
      if (!user) throw new Error(t("phase8.profile.errors.notAuthenticated"));

      // Buscar foto atual
      const { data: currentProfile } = await supabase
        .from("profiles")
        .select("avatar_url")
        .eq("id", user.id)
        .single();

      if (!currentProfile?.avatar_url) {
        throw new Error(t("phase8.profile.errors.noPhotoToDelete"));
      }

      // Extrair o nome do arquivo da URL
      const urlParts = currentProfile.avatar_url.split("/");
      const fileName = urlParts[urlParts.length - 1];
      const filePath = `${user.id}/${fileName}`;

      // Deletar do storage
      const { error: deleteError } = await supabase.storage
        .from("profile-photos")
        .remove([filePath]);

      if (deleteError) throw deleteError;

      // Atualizar perfil
      const { error: updateError } = await supabase
        .from("profiles")
        .update({ avatar_url: null })
        .eq("id", user.id);

      if (updateError) throw updateError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["profile"] });
      toast({
        title: t("phase8.profile.photoDeletedTitle"),
        description: t("phase8.profile.photoDeletedDescription"),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t("phase8.profile.photoDeleteErrorTitle"),
        description: error.message || t("phase8.profile.errors.photoDeleteFailed"),
        variant: "destructive",
      });
    },
  });
};
